import React, { useState } from 'react';
import { CircularProgress, MenuItem, TextField } from '@mui/material';
import PopupBase from '../../../../../popup/base/PopupBase';
import CustomButton from '../../../../../components/buttons/CustomButton';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { cleanMoneyTyping, formatMoneyInput, parseMoney } from '../../../../../utils/moneyUtils';
import TussSearchField from './TussSearchField';
import formStyles from './ProviderFormPopup.module.css';
import styles from './InsuranceProcedures.module.css';

const fromProcedure = (procedure) => ({
    tuss_code: procedure?.tuss_code || '',
    code: procedure?.code || '',
    name: procedure?.name || '',
    price: procedure?.price != null ? formatMoneyInput(procedure.price) : '',
    procedure_id: procedure?.procedure_id != null ? String(procedure.procedure_id) : ''
});

/**
 * Incluir ou editar um procedimento na tabela de uma operadora. A busca na TUSS
 * preenche código e nome; os dois continuam editáveis e, sem TUSS, vira um
 * procedimento próprio. O procedimento da agenda faz o atendimento por convênio
 * sugerir este código sozinho.
 */
function InsuranceProcedureFormPopup({ providerId, providerName, procedure, onSaved, onClose }) {
    const { CompanyConfig, SetInsuranceProcedure, UpdateInsuranceProcedure } = DataProviderInstance();
    const isEdit = !!procedure;
    const [form, setForm] = useState(fromProcedure(procedure));
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const agendaProcedures = (CompanyConfig?.Procedures || []).filter((item) =>
        Number(item.Active) !== 0 || String(item.Id) === form.procedure_id);

    const change = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => (prev[name] || prev.general ? { ...prev, [name]: undefined, general: undefined } : prev));
    };

    const pickTuss = (option) => {
        setForm((prev) => ({ ...prev, tuss_code: option.code, code: option.code, name: option.name }));
        setErrors({});
    };

    const submit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const next = {};
        if (!form.code.trim()) next.code = 'Informe o código';
        if (form.name.trim().length < 2) next.name = 'Informe o nome';
        const price = form.price.trim() === '' ? null : parseMoney(form.price);
        if (form.price.trim() !== '' && price === null) next.price = 'Valor inválido';
        setErrors(next);
        if (Object.keys(next).length > 0) return;

        const data = {
            code: form.code.trim(),
            name: form.name.trim(),
            price: price === null ? '' : price,
            procedure_id: form.procedure_id
        };
        setSaving(true);
        const done = (result) => {
            setSaving(false);
            if (result?.status === 'SUCCESS') {
                onSaved(isEdit ? 'Procedimento atualizado' : 'Procedimento incluído');
            } else {
                setErrors({ general: result?.error || 'Não foi possível salvar. Tente de novo.' });
            }
        };
        if (isEdit) {
            UpdateInsuranceProcedure({ ...data, id: procedure.id }, done);
        } else {
            SetInsuranceProcedure({ ...data, provider_id: providerId, tuss_code: form.tuss_code }, done);
        }
    };

    const content = (
        <form className={formStyles.content} onSubmit={submit} noValidate>
            {errors.general && <div className={formStyles.error} role="alert">{errors.general}</div>}

            {!isEdit && (
                <section className={formStyles.section}>
                    <h4 className={formStyles.sectionTitle}>Buscar na TUSS</h4>
                    <TussSearchField disabled={saving} onPick={pickTuss} />
                    <span className={formStyles.hint}>
                        Escolha da tabela oficial ou preencha código e nome abaixo para um procedimento próprio.
                    </span>
                </section>
            )}

            <section className={formStyles.section}>
                <h4 className={formStyles.sectionTitle}>Procedimento na {providerName}</h4>
                {form.tuss_code && (
                    <span className={formStyles.hint}>
                        Origem TUSS {form.tuss_code}. Código e nome podem ser ajustados para esta operadora.
                    </span>
                )}
                <div className={styles.formRow}>
                    <div className={`${formStyles.field} ${styles.codeField}`}>
                        <label className={formStyles.label} htmlFor="insurance-procedure-code">Código *</label>
                        <TextField
                            id="insurance-procedure-code"
                            value={form.code}
                            onChange={(e) => change('code', e.target.value)}
                            disabled={saving}
                            size="small"
                            error={!!errors.code}
                            helperText={errors.code}
                            className={formStyles.input}
                            inputProps={{ maxLength: 20 }}
                        />
                    </div>
                    <div className={`${formStyles.field} ${styles.priceField}`}>
                        <label className={formStyles.label} htmlFor="insurance-procedure-price">Valor</label>
                        <TextField
                            id="insurance-procedure-price"
                            value={form.price}
                            onChange={(e) => change('price', cleanMoneyTyping(e.target.value))}
                            onBlur={() => {
                                const value = parseMoney(form.price);
                                if (form.price.trim() !== '' && value !== null) change('price', formatMoneyInput(value));
                            }}
                            disabled={saving}
                            size="small"
                            placeholder="0,00"
                            error={!!errors.price}
                            helperText={errors.price}
                            className={formStyles.input}
                            InputProps={{ startAdornment: <span className={styles.currency}>R$</span> }}
                            inputProps={{ inputMode: 'decimal' }}
                        />
                    </div>
                </div>
                <div className={formStyles.field}>
                    <label className={formStyles.label} htmlFor="insurance-procedure-name">Nome *</label>
                    <TextField
                        id="insurance-procedure-name"
                        value={form.name}
                        onChange={(e) => change('name', e.target.value)}
                        disabled={saving}
                        size="small"
                        multiline
                        maxRows={3}
                        error={!!errors.name}
                        helperText={errors.name}
                        className={formStyles.input}
                        inputProps={{ maxLength: 400 }}
                    />
                </div>
            </section>

            <section className={formStyles.section}>
                <h4 className={formStyles.sectionTitle}>Na agenda</h4>
                <div className={formStyles.field}>
                    <label className={formStyles.label} htmlFor="insurance-procedure-agenda">Procedimento da agenda</label>
                    <TextField
                        id="insurance-procedure-agenda"
                        select
                        value={form.procedure_id}
                        onChange={(e) => change('procedure_id', e.target.value)}
                        disabled={saving}
                        size="small"
                        helperText="Opcional. Ao marcar este procedimento por esta operadora, o código já vem escolhido."
                        className={formStyles.input}
                        SelectProps={{ displayEmpty: true }}
                    >
                        <MenuItem value="">Nenhum</MenuItem>
                        {agendaProcedures.map((item) => (
                            <MenuItem key={item.Id} value={String(item.Id)}>{item.Label}</MenuItem>
                        ))}
                    </TextField>
                </div>
            </section>

            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
    );

    const footer = (
        <div className={formStyles.footer}>
            <CustomButton style="primary" variant="text" label="Cancelar" onClick={onClose} enabled={!saving} />
            <CustomButton
                style="main"
                variant="contained"
                label={isEdit ? 'Salvar' : 'Incluir procedimento'}
                icon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={submit}
                enabled={!saving}
            />
        </div>
    );

    return (
        <PopupBase
            Title={isEdit ? 'Editar procedimento' : 'Novo procedimento'}
            Content={content}
            Footer={footer}
            OnClose={onClose}
            maxWidth="sm"
        />
    );
}

export default InsuranceProcedureFormPopup;
